import React, { useState, useRef } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Download, Image as ImageIcon, X } from "lucide-react";

async function fileToBase64(file) {
  const b = await file.arrayBuffer();
  const bin = String.fromCharCode(...new Uint8Array(b));
  return btoa(bin);
}

export default function BulkTab({ onRun }) {
  const [names, setNames] = useState("");
  const [images, setImages] = useState([]); // { b64, name, mime, preview }
  const [platform, setPlatform] = useState("etsy");
  const [tone, setTone] = useState("professional");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const addFiles = async (files) => {
    const accepted = [];
    for (const f of Array.from(files)) {
      if (!/image\/(jpeg|jpg|png|webp)/i.test(f.type)) continue;
      const b64 = await fileToBase64(f);
      accepted.push({ b64, name: f.name, mime: f.type, preview: `data:${f.type};base64,${b64}` });
    }
    setImages(prev => [...prev, ...accepted]);
    if (accepted.length) toast.success(`Added ${accepted.length} image${accepted.length>1?"s":""}`);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) await addFiles(e.dataTransfer.files);
  };
  const onPick = async (e) => { if (e.target.files?.length) await addFiles(e.target.files); };
  const removeImage = (i) => setImages(prev => prev.filter((_, idx) => idx !== i));

  const run = async () => {
    const list = names.split("\n").map(x=>x.trim()).filter(Boolean);
    const total = list.length + images.length;
    if (!total) { toast.error("Add product names or drop images"); return; }
    setLoading(true); setRows([]);
    const out = [];
    // 1) names
    for (const name of list) {
      try {
        const r = await http.post("/generate", {
          productName: name, platforms: [platform], tone,
          language: "English", cleanMode: true, keywords: [],
        });
        const l = r.data.listings?.[0];
        if (l) out.push({ name, ...l });
      } catch (e) {
        out.push({ name, error: e?.response?.data?.detail || "failed" });
        if (e?.response?.status === 402) { toast.error("Out of credits"); setRows([...out]); setLoading(false); return; }
      }
      setRows([...out]);
    }
    // 2) images
    for (const img of images) {
      try {
        const r = await http.post("/generate", {
          productName: null, platforms: [platform], tone,
          language: "English", cleanMode: true, keywords: [],
          imageBase64: img.b64, imageMime: img.mime,
        });
        const l = r.data.listings?.[0];
        if (l) out.push({ name: img.name, image: img.preview, ...l });
      } catch (e) {
        out.push({ name: img.name, error: e?.response?.data?.detail || "failed" });
        if (e?.response?.status === 402) { toast.error("Out of credits"); setRows([...out]); setLoading(false); return; }
      }
      setRows([...out]);
    }
    setLoading(false);
    toast.success(`Bulk done · ${out.length}/${total}`);
    onRun && onRun();
  };

  const csv = () => {
    const header = ["name","titleSeo","descriptionShort","tags","altText"];
    const escape = (v) => `"${String(v||"").replaceAll('"','""')}"`;
    const lines = [header.join(",")];
    for (const r of rows) {
      lines.push([r.name, r.titleSeo, r.descriptionShort, (r.tags||[]).join("; "), r.altText].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "tagtitan_bulk.csv"; a.click();
  };

  return (
    <div className="space-y-5">
      <div className="tt-card p-6">
        <h1 className="text-3xl font-black h-tight">Bulk mode</h1>
        <p className="text-sm text-zinc-500 mt-1">Drop multiple design images <span className="font-semibold">or</span> paste product names, one per line. 1 credit each. Download the result as CSV.</p>

        <div className="mt-5 grid md:grid-cols-2 gap-4">
          {/* Multi-image drop */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Drop designs</div>
            <label
              data-testid="bulk-drop-zone"
              onDrop={onDrop} onDragOver={(e)=>e.preventDefault()}
              className="flex flex-col items-center justify-center border-2 border-dashed border-zinc-300 rounded-xl p-6 cursor-pointer bg-zinc-50/60 hover:border-lime hover:bg-lime/5 transition-colors min-h-[180px]"
            >
              <input ref={fileRef} data-testid="bulk-file-input" type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPick} />
              <ImageIcon size={26} className="text-zinc-400" />
              <div className="mt-2 text-sm font-semibold">Drop images here</div>
              <div className="text-xs text-zinc-500">or click · JPG, PNG, WEBP · unlimited</div>
              {images.length > 0 && (
                <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2 w-full">
                  {images.map((img, i) => (
                    <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-zinc-200 bg-white">
                      <img src={img.preview} alt="" className="w-full h-full object-cover" />
                      <button
                        data-testid={`bulk-remove-${i}`}
                        onClick={(e)=>{ e.preventDefault(); removeImage(i); }}
                        className="absolute top-0.5 right-0.5 bg-white rounded-full p-0.5 shadow border border-zinc-200"
                      ><X size={11} /></button>
                    </div>
                  ))}
                </div>
              )}
            </label>
          </div>

          {/* Names + settings */}
          <div className="grid gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Or paste names (one per line)</div>
              <textarea data-testid="bulk-names" className="input min-h-[130px]" placeholder={"Astro Cat Tee\nMountain Sunrise Mug\nMinimalist Skull Poster"} value={names} onChange={e=>setNames(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Platform</div>
                <select className="input" value={platform} onChange={e=>setPlatform(e.target.value)}>
                  <option value="etsy">Etsy</option>
                  <option value="amazon_merch">Amazon Merch</option>
                  <option value="redbubble">Redbubble</option>
                  <option value="shopify">Shopify</option>
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Tone</div>
                <select className="input" value={tone} onChange={e=>setTone(e.target.value)}>
                  <option>professional</option><option>playful</option><option>minimal</option><option>vintage</option><option>streetwear</option><option>luxury</option>
                </select>
              </div>
            </div>
            <button data-testid="bulk-run" disabled={loading} onClick={run} className="btn-primary">{loading ? `Running… (${rows.length})` : `Run bulk (${names.split("\n").filter(x=>x.trim()).length + images.length} items)`}</button>
          </div>
        </div>
      </div>

      {!!rows.length && (
        <div className="tt-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Results ({rows.length})</div>
            <button data-testid="bulk-csv-btn" onClick={csv} className="btn-dark inline-flex items-center gap-1 text-sm"><Download size={14} /> CSV</button>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <tr><th className="p-2 w-12"></th><th className="p-2">Source</th><th className="p-2">Title</th><th className="p-2">Tags</th></tr>
              </thead>
              <tbody>
                {rows.map((r,i)=>(
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="p-2">{r.image ? <img src={r.image} alt="" className="w-8 h-8 rounded object-cover" /> : null}</td>
                    <td className="p-2 font-semibold truncate max-w-[180px]">{r.name}</td>
                    <td className="p-2">{r.error ? <span className="text-red-500">{r.error}</span> : r.titleSeo}</td>
                    <td className="p-2 text-zinc-600">{(r.tags||[]).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
